import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Handshake, ChevronRight, BookOpen, Shield, Heart, RefreshCw } from 'lucide-react';

const CONFLICT_CONTENT = [
  {
    id: 'basics',
    title: 'Grunderna i konflikthantering',
    icon: Handshake,
    description: 'Hantera konflikter på ett hälsosamt sätt.',
    content: [
      '**Undvik inte:** Olösta konflikter växer ofta och skapar avstånd.',
      '**Sök förståelse:** Försök se situationen från den andras perspektiv.',
      '**Fokusera på problemet:** Attackera inte personen. "Jag är frustrerad över situationen" istället för "Du är så slarvig".',
      '**Kompromissa:** Ibland behöver båda ge lite för att komma framåt.',
      '**Ta pauser:** Om det hettar till, säg "Jag behöver 20 minuter, sen fortsätter vi".',
      '**Sov aldrig på en olöst konflikt** – försök åtminstone komma överens om att fortsätta samtalet.'
    ],
    questions: [
      'Hur reagerar du vanligtvis i konflikter?',
      'Undviker du konflikter eller söker du dem?',
      'Vad är du rädd ska hända om du står upp för dig själv?',
      'Kan du skilja på personen och problemet?'
    ]
  },
  {
    id: 'fair-fighting',
    title: 'Regler för schysst bråk',
    icon: Shield,
    description: 'Även par som älskar varandra bråkar – men det finns regler.',
    content: [
      '**Håll er till ämnet:** Dra inte upp gamla grejer som är lösta.',
      '**Inga generaliseringar:** Undvik "du gör alltid" och "du gör aldrig".',
      '**Inga personangrepp:** Kritisera beteendet, inte personen.',
      '**Ingen tyst behandling:** Att stänga ute är destruktivt.',
      '**Ta ansvar:** Säg "jag hade fel" när det behövs.',
      '**Ha ett slutmål:** Vad vill ni uppnå med samtalet?',
      '**Reparera efteråt:** Säg förlåt, krama, bekräfta att ni fortfarande älskar varandra.'
    ],
    questions: [
      'Vilka "dåliga vanor" har ni när ni bråkar?',
      'Brukar ni lösa konflikter eller bara tystna?',
      'Hur mår du efter en konflikt – lättad eller tyngd?',
      'Vad kan ni göra bättre nästa gång?'
    ]
  },
  {
    id: 'repair',
    title: 'Reparation efter bråk',
    icon: RefreshCw,
    description: 'Hur ni läker och kommer närmare efter en konflikt.',
    content: [
      '**Låt känslorna landa:** Vänta tills ni båda är lugna innan ni försöker prata igen.',
      '**Erkänn din del:** Säg vad du kunde gjort annorlunda.',
      '**Be om förlåtelse:** En genuin ursäkt innehåller inga "men".',
      '**Lyssna på smärtan:** Låt din partner berätta hur hen påverkades.',
      '**Planera framåt:** Hur undviker ni samma konflikt igen?',
      '**Fysisk närhet:** En kram eller hand att hålla kan läka mycket.',
      '**Ge det tid:** Tillit byggs inte upp på en dag.'
    ],
    questions: [
      'Hur brukar ni återhämta er efter bråk?',
      'Är du bra på att be om ursäkt genuint?',
      'Kan du förlåta utan att hålla agg?',
      'Lär ni er något av era konflikter?'
    ]
  },
  {
    id: 'triggers',
    title: 'Förstå dina triggers',
    icon: Heart,
    description: 'Varför vissa saker triggar dig mer än andra.',
    content: [
      '**Triggers är personliga:** Det som triggar dig kanske inte triggar andra.',
      '**Roten är ofta i det förflutna:** Gamla sår kan aktiveras av nya situationer.',
      '**Reaktionen är större än situationen:** Om du överreagerar, fråga dig varför.',
      '**Identifiera mönster:** Vad händer precis innan du blir arg/ledsen/rädd?',
      '**Kommunicera triggers:** Berätta för din partner vad som triggar dig och varför.',
      '**Ta ansvar:** Dina triggers är ditt ansvar att hantera, även om andra kan vara hänsynsfulla.'
    ],
    questions: [
      'Vad triggar dig mest i relationer?',
      'Var kommer dessa triggers ifrån?',
      'Hur brukar du reagera när du blir triggad?',
      'Har du berättat för din partner om dina triggers?'
    ]
  },
  {
    id: 'deescalate',
    title: 'Trappa ner en konflikt',
    icon: Handshake,
    description: 'Tekniker för att lugna ner en het situation.',
    content: [
      '**Ta ett steg tillbaka:** Fysiskt avstånd kan hjälpa.',
      '**Andas djupt:** Aktiverar parasympatiska nervsystemet.',
      '**Sänk rösten:** Lugn röst smittar av sig.',
      '**Validera:** "Jag förstår att du är arg" – det betyder inte att du håller med.',
      '**Be om paus:** "Jag vill lösa detta, men behöver 15 minuter först."',
      '**Undvik "men":** "Jag hör dig, men..." avfärdar det du just sa.',
      '**Sök gemensam mark:** "Vi vill båda samma sak – att må bra ihop."'
    ],
    questions: [
      'Vad gör du när en konflikt eskalerar?',
      'Kan du pausa mitt i en het diskussion?',
      'Hur känns det i kroppen när du är i konflikt?'
    ]
  }
];

export default function ConflictPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [selectedTopic, setSelectedTopic] = useState(null);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="conflict-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Konflikthantering</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till konfliktverktyg.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (selectedTopic) {
    const topic = CONFLICT_CONTENT.find(t => t.id === selectedTopic);
    return (
      <div className="min-h-screen bg-background" data-testid="conflict-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedTopic(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Konflikthantering</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-amber-50 rounded-2xl p-8">
            <topic.icon className="w-10 h-10 text-amber-600 mb-4" />
            <h2 className="font-heading text-2xl font-semibold mb-2">{topic.title}</h2>
            <p className="text-muted-foreground">{topic.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            {topic.content.map((text, i) => (
              <p key={i} className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </div>

          <div className="bg-primary/5 rounded-2xl p-6 space-y-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Reflektionsfrågor
            </h3>
            {topic.questions.map((q, i) => (
              <div key={i} className="flex gap-3 items-start p-4 rounded-xl bg-white">
                <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">{i + 1}</span>
                <span>{q}</span>
              </div>
            ))}
          </div>

          {/* Contact */}
          
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="conflict-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Konflikthantering</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-3">
          <Handshake className="w-12 h-12 mx-auto text-amber-500" />
          <h2 className="font-heading text-2xl font-semibold">Konflikthantering</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Lär dig hantera konflikter konstruktivt och reparera relationen efteråt.
          </p>
        </div>

        <div className="space-y-3">
          {CONFLICT_CONTENT.map((topic) => (
            <button
              key={topic.id}
              onClick={() => setSelectedTopic(topic.id)}
              className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
                  <topic.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{topic.title}</h3>
                  <p className="text-sm text-muted-foreground">{topic.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
              </div>
            </button>
          ))}
        </div>

        {/* Quick tip */}
        <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
          <p className="text-amber-800 text-sm">
            💡 <strong>Kom ihåg:</strong> Målet med en konflikt är inte att vinna – det är att förstå varandra bättre.
          </p>
        </div>

        {/* Contact */}
      </main>
    </div>
  );
}
